// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file AlertDialog.tsx
 * @input Uses React, Dialog, Layout, Heading, Text, Button
 * @output Exports AlertDialog component, AlertDialogProps type
 * @position Core implementation; consumed by index.ts, tested by AlertDialog.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/AlertDialog/AlertDialog.doc.mjs (props table, features, examples)
 * - /packages/core/src/AlertDialog/AlertDialog.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/AlertDialog/index.ts (exports if types change)
 * - /apps/storybook/stories/AlertDialog.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/AlertDialog/ (showcase blocks)
 */
import React, { useId, useCallback } from 'react';
import { Dialog } from "../Dialog/index.js";
import { Layout } from "../Layout/Layout.js";
import { LayoutContent } from "../Layout/LayoutContent.js";
import { LayoutFooter } from "../Layout/LayoutFooter.js";
import { HStack } from "../Stack/index.js";
import { Heading } from "../Heading/Heading.js";
import { Text } from "../Text/Text.js";
import { Button } from "../Button/index.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A confirmation dialog for destructive or irreversible actions.
 *
 * Uses `role="alertdialog"` and requires explicit user action to dismiss.
 * Cannot be dismissed by clicking outside. Escape key triggers cancel.
 * Initial focus goes to the cancel button (least destructive action).
 *
 * @example
 * ```
 * <AlertDialog
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Delete item?"
 *   description="This action cannot be undone."
 *   actionLabel="Delete"
 *   onAction={async () => { await deleteItem(); setIsOpen(false); }}
 * />
 * ```
 */
export function AlertDialog({
  ref,
  isOpen,
  isInline,
  onOpenChange,
  title,
  description,
  cancelLabel: cancelLabelFromProps,
  actionLabel,
  actionVariant = 'destructive',
  isActionLoading,
  onAction,
  width = 400,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ...rest
}) {
  const t = useTranslator();
  const cancelLabel = cancelLabelFromProps ?? t('@astryx.alertDialog.cancel');
  const titleId = useId();
  const descriptionId = useId();
  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);
  return /*#__PURE__*/_jsx(Dialog, {
    ...rest,
    ref: ref,
    isOpen: isOpen,
    isInline: isInline,
    onOpenChange: onOpenChange,
    width: width,
    purpose: "form",
    role: "alertdialog",
    "aria-labelledby": titleId,
    "aria-describedby": descriptionId,
    ...mergeProps(themeProps('alert-dialog'), {
      className,
      style
    }),
    xstyle: xstyle,
    "data-testid": testId,
    children: /*#__PURE__*/_jsx(Layout, {
      content: /*#__PURE__*/_jsxs(LayoutContent, {
        children: [/*#__PURE__*/_jsx(Heading, {
          level: 2,
          id: titleId,
          children: title
        }), /*#__PURE__*/_jsx(Text, {
          type: "body",
          color: "secondary",
          id: descriptionId,
          children: description
        })]
      }),
      footer: /*#__PURE__*/_jsx(LayoutFooter, {
        children: /*#__PURE__*/_jsxs(HStack, {
          gap: 2,
          hAlign: "end",
          children: [/*#__PURE__*/_jsx(Button, {
            variant: "ghost",
            label: cancelLabel,
            onClick: handleCancel
          }), /*#__PURE__*/_jsx(Button, {
            variant: actionVariant,
            label: actionLabel,
            onClick: onAction,
            isLoading: isActionLoading
          })]
        })
      })
    })
  });
}
AlertDialog.displayName = 'AlertDialog';